import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from 'src/contexts';
import { useHttp, useNotification } from 'src/hooks';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import LoadingSpinner from 'src/components/common/ui/loader/LoadingSpinner';
import Select from 'react-select';

interface Props {
  businessId: string;
  selectedAccount: string | null;
  setSelectedAccount: (accountId: string) => void;
  setIsStepValid: (isValid: boolean) => void;
}

const CreateAdAccount: React.FC<Props> = ({
  businessId,
  selectedAccount,
  setSelectedAccount,
  setIsStepValid,
}) => {
  // First check for loading state before any hooks
  const notify = useNotification();
  const { sendRequest } = useHttp();
  const { sendRequest: sendCreateAdReq, isLoading: isCreatingAdAccount, error } = useHttp();
  const { accessToken } = useContext(AuthContext);
  const [loadingAdAccounts, setLoadingAdAccounts] = useState(false);
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);
  const [loadingTimezones, setLoadingTimezones] = useState(false);

  const [adAccounts, setAdAccounts] = useState<{ name: string }[]>([]);
  const [currencies, setCurrencies] = useState([]);
  const [timezones, setTimezones] = useState([]);

  useEffect(() => {
    setIsStepValid(!!selectedAccount);
  }, [selectedAccount, setIsStepValid]);

  useEffect(() => {
    if (!businessId) return;
    setLoadingAdAccounts(true);
    sendRequest(
      {
        url: `ads/ad-accounts?businessId=${businessId}`,
        accessToken,
      },
      (data: any) => {
        if (data) {
          setAdAccounts(data || []);
          setLoadingAdAccounts(false);
        }
      },
    );
  }, [businessId, accessToken]);

  useEffect(() => {
    setLoadingCurrencies(true);
    sendRequest(
      {
        url: `currency`,
        accessToken,
      },
      data => {
        const formattedCurrencies = data.map((currency: any) => ({
          value: currency.code,
          label: `${currency.name} (${currency.code})`,
        }));
        setCurrencies(formattedCurrencies);
        setLoadingCurrencies(false);
      },
    );
  }, [businessId, accessToken]);

  useEffect(() => {
    setLoadingTimezones(true);
    sendRequest(
      {
        url: `timezones`,
        accessToken,
      },
      data => {
        const formattedTimezones = data.map((timezone: any) => ({
          value: timezone.timezoneId,
          label: `${timezone.offset}`,
        }));
        setTimezones(formattedTimezones);
        setLoadingTimezones(false);
      },
    );
  }, [businessId, accessToken]);

  const formik = useFormik({
    initialValues: {
      name: '',
      currency: '',
      timezoneId: '',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required'),
      currency: Yup.string().required('Currency is required'),
      timezoneId: Yup.number().required('Timezone ID is required'),
    }),
    onSubmit: values => {
      if (!businessId) return;
      sendCreateAdReq(
        {
          url: 'ads/create-ad-account',
          method: 'POST',
          payload: {
            name: values.name,
            businessId: businessId,
            currency: values.currency,
            timezoneId: values.timezoneId,
          },
          accessToken,
        },
        data => {
          if (data) {
            notify('Ad account Created successfully!', 'success');
            setAdAccounts(prev => [...prev, { name: values.name }]);
          }
        },
      );
    },
  });

  useEffect(() => {
    if (error) {
      notify(error || '', 'error');
    }
  }, [error]);

  // Instead of early return, include loading state in JSX
  if (isCreatingAdAccount || loadingAdAccounts) {
    return (
      <div className="flex items-center justify-center min-h-[80vh]">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="my-8 p-6 border border-gray-300 rounded-2xl max-w-2xl mx-auto">
      {/* Rest of your JSX */}
      <h2 className="text-2xl text-center font-semibold mb-6">
        {adAccounts.length > 0 ? 'Ad Accounts' : 'Create Ad Account'}
      </h2>

      {adAccounts.length > 0 ? (
        <ul className="py-56 flex flex-wrap justify-center gap-4">
          {adAccounts.map((account, index) => (
            <div
              key={account.account_id}
              className={`flex items-center space-x-4 p-4 border rounded-lg cursor-pointer transition-all
             ${
               selectedAccount === account.account_id
                 ? 'border-blue-500 bg-blue-100'
                 : 'border-gray-300 hover:bg-gray-100'
             }`}
              onClick={() => setSelectedAccount(account.account_id)}
            >
              <div className="w-10 h-10 flex items-center justify-center bg-gray-700 text-white font-bold rounded-full">
                {account.name.charAt(0).toUpperCase()}
              </div>
              <span className="text-lg font-medium">{account.name}</span>
            </div>
          ))}
        </ul>
      ) : (
        <>
          <p className="text-center text-gray-500">No ad accounts found. Create a new one below.</p>
          <form onSubmit={formik.handleSubmit} className="mt-6 space-y-4">
            <input
              type="text"
              name="name"
              placeholder="Ad Account Name"
              className="w-full p-3 border border-gray-400 rounded-md"
              value={formik.values.name}
              onChange={formik.handleChange}
            />
            {formik.errors.name && <p className="text-red-500">{formik.errors.name}</p>}

            <Select
              options={currencies}
              isLoading={loadingCurrencies}
              placeholder="Search & Select Currency..."
              onChange={selectedOption => {
                formik.setFieldValue('currency', selectedOption?.value);
              }}
              value={currencies.find(option => option.value === formik.values.currency) || null}
              className="w-full"
            />
            {formik.errors.currency && <p className="text-red-500">{formik.errors.currency}</p>}

            <Select
              options={timezones}
              isLoading={loadingTimezones}
              placeholder="Search & Select Timezone..."
              onChange={selectedOption => {
                formik.setFieldValue('timezoneId', selectedOption?.value);
              }}
              value={timezones.find(option => option.value === formik.values.timezoneId) || null}
              className="w-full"
            />
            {formik.errors.timezoneId && <p className="text-red-500">{formik.errors.timezoneId}</p>}

            <button type="submit" className="w-full bg-green-600 text-white py-2 rounded-md">
              Create Ad Account
            </button>
          </form>
        </>
      )}
    </div>
  );
};

export default CreateAdAccount;
